import {
  applySignalsConfig,
  applySignalsLayout,
  applySignalsTraces,
  changePct,
  compact,
  figureTitle,
  figureUnit,
  normaliseTimeAxes
} from '@/lib/plotlyTheme';
import { buildXlsx, downloadXlsx } from '@/lib/xlsx';
import { ArrowLeft, Download, Table2 } from 'lucide-react';
import { Suspense, lazy, useMemo, useRef, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';

const Plot = lazy(() => import('react-plotly.js'));

/**
 * A generated chart, in the same card shell the data table uses, so an answer
 * carrying both reads as one thing rather than a framed table beside a loose
 * plot.
 *
 * The card owns the title, the unit and the legend; the figure itself is
 * stripped of all three (see plotlyTheme). That is what removes the vertically
 * rotated "EGP" and stops a figure title colliding with the header above it.
 *
 * **Drill-down is deliberately client-side only.** Clicking a category shows
 * the numbers already in the figure — each series' value, the change between
 * them, and that category's share of the total. It never asks the sandbox for
 * detail rows: the container is sized for the answer it already produced, and
 * a click that triggers a fresh query against a detail table is exactly the
 * kind of load it cannot absorb.
 */

interface Props {
  data: any[];
  layout: any;
  frames?: any;
  config?: any;
  className?: string;
}

interface Series {
  name: string;
  color: string;
  /** Category -> value, in the figure's own category order. */
  byCategory: Map<string, number>;
}

const FALLBACK_COLORS = ['#0080E0', '#BCD8F4', '#6E9FF7', '#04101A', '#8FBFEA'];

/** Pulls a tidy category/series view out of whatever traces the figure has. */
const readSeries = (data: any[], colorway: string[]) => {
  const categories: string[] = [];
  const series: Series[] = [];

  data.forEach((trace, i) => {
    const xs: any[] = Array.isArray(trace?.x) ? trace.x : [];
    const ys: any[] = Array.isArray(trace?.y) ? trace.y : [];
    // Horizontal bars put the categories on y.
    const horizontal = trace?.orientation === 'h';
    const cats = (horizontal ? ys : xs).map((v) => String(v));
    const vals = (horizontal ? xs : ys).map((v) => Number(v));

    const byCategory = new Map<string, number>();
    cats.forEach((c, j) => {
      if (!categories.includes(c)) categories.push(c);
      if (Number.isFinite(vals[j])) byCategory.set(c, vals[j]);
    });

    series.push({
      name: String(trace?.name ?? `Series ${i + 1}`),
      color:
        trace?.marker?.color && typeof trace.marker.color === 'string'
          ? trace.marker.color
          : colorway[i % colorway.length] ||
            FALLBACK_COLORS[i % FALLBACK_COLORS.length],
      byCategory
    });
  });

  return { categories, series };
};

const toolbarButton =
  'sig-toolbar-btn flex items-center gap-[5px] text-[11.5px] font-medium rounded-[7px] px-[9px] py-[5px] whitespace-nowrap transition-colors';

export const ChartCard = ({
  data,
  layout,
  frames,
  config,
  className
}: Props) => {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [focus, setFocus] = useState<string | null>(null);
  const plotRef = useRef<any>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const title = figureTitle(layout);
  const unit = figureUnit(layout);

  const themedLayout = useMemo(
    () => normaliseTimeAxes(applySignalsLayout(layout), data || []),
    [layout, data]
  );
  const themedData = useMemo(() => applySignalsTraces(data || []), [data]);
  const themedConfig = useMemo(() => applySignalsConfig(config), [config]);

  const { categories, series } = useMemo(
    () => readSeries(data || [], themedLayout.colorway || FALLBACK_COLORS),
    [data, themedLayout.colorway]
  );

  // 236 was sized for the narrow container the chart used to sit in. Now that
  // the card is full width, a bar chart needs room for its category labels —
  // a figure that sets its own height still wins.
  const height = layout?.height || 340;

  const downloadExcel = () => {
    const header = ['Category', ...series.map((s) => s.name)];
    const rows = categories.map((c) => [
      { text: c, value: null },
      ...series.map((s) => {
        const v = s.byCategory.get(c);
        return { text: v === undefined ? '' : String(v), value: v ?? null };
      })
    ]);
    downloadXlsx(
      buildXlsx(header, rows, title || 'Chart'),
      `${(title || 'signals-chart').replace(/[\\/:*?"<>|]+/g, '').slice(0, 60) || 'signals-chart'}.xlsx`
    );
  };

  // ── drill-down: everything below comes from the figure already in memory ──
  const focusRow = useMemo(() => {
    if (!focus) return null;
    const values = series.map((s) => ({
      name: s.name,
      color: s.color,
      value: s.byCategory.get(focus)
    }));
    const totals = series.map((s) =>
      Array.from(s.byCategory.values()).reduce((a, b) => a + b, 0)
    );
    // Two series of the same measure read as older -> current, which is the
    // order the sandbox is told to emit them in.
    const change =
      values.length >= 2 &&
      typeof values[values.length - 1].value === 'number' &&
      typeof values[0].value === 'number'
        ? changePct(
            values[values.length - 1].value as number,
            values[0].value as number
          )
        : null;
    const shares = values.map((v, i) =>
      typeof v.value === 'number' && totals[i]
        ? (v.value / totals[i]) * 100
        : null
    );
    return { values, change, shares };
  }, [focus, series]);

  return (
    <div
      ref={cardRef}
      className={className}
      style={{
        border: '1px solid var(--sig-border)',
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--sig-surface)'
      }}
    >
      <div
        className="sig-toolbar flex items-center justify-between gap-3"
        style={{
          padding: '9px 12px',
          borderBottom: '1px solid var(--sig-border-light)',
          background: 'var(--sig-panel)'
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {focus ? (
            <button
              type="button"
              onClick={() => setFocus(null)}
              className="flex items-center gap-1 text-[12px] font-medium"
              style={{ color: 'var(--sig-blue-deep)' }}
            >
              <ArrowLeft size={13} strokeWidth={2.2} />
              All categories
            </button>
          ) : (
            <>
              {title ? (
                <span
                  dir="auto"
                  className="text-[13px] font-semibold truncate"
                  style={{ color: 'var(--sig-ink)' }}
                >
                  {title}
                </span>
              ) : null}
              {unit ? (
                <span
                  dir="ltr"
                  className="text-[11.5px] whitespace-nowrap"
                  style={{
                    color: 'var(--sig-subtle)',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {unit}
                </span>
              ) : null}
            </>
          )}
        </div>

        <div
          className="flex items-center gap-1"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <button
            type="button"
            onClick={() => setView((v) => (v === 'chart' ? 'table' : 'chart'))}
            className={toolbarButton}
            style={{
              border: '1px solid var(--sig-border-strong)',
              background:
                view === 'table' ? 'var(--sig-tint)' : 'var(--sig-surface)',
              color: 'var(--sig-body)'
            }}
          >
            <Table2 size={12} strokeWidth={2.2} />
            {view === 'chart' ? 'Table' : 'Chart'}
          </button>
          <button
            type="button"
            onClick={downloadExcel}
            className={`${toolbarButton} font-semibold`}
            style={{
              background: 'var(--sig-blue)',
              color: '#fff',
              padding: '5px 10px'
            }}
          >
            <Download size={12} strokeWidth={2.2} />
            Excel
          </button>
        </div>
      </div>

      <div
        className="flex flex-col gap-[14px]"
        style={{ padding: '16px 18px 14px' }}
      >
        {series.length > 1 && !focus ? (
          <div className="flex flex-wrap items-center" style={{ gap: 16 }}>
            {series.map((s) => (
              <span
                key={s.name}
                className="flex items-center"
                style={{ gap: 7 }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: s.color,
                    display: 'block',
                    flex: 'none'
                  }}
                />
                <span
                  dir="auto"
                  className="text-[12px]"
                  style={{ color: 'var(--sig-muted)' }}
                >
                  {s.name}
                </span>
              </span>
            ))}
          </div>
        ) : null}

        {focus && focusRow ? (
          <div className="flex flex-col gap-3">
            <div
              dir="auto"
              className="text-[15px] font-semibold"
              style={{ color: 'var(--sig-ink)' }}
            >
              {focus}
            </div>
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              {focusRow.values.map((v, i) => (
                <div
                  key={v.name}
                  style={{
                    border: '1px solid var(--sig-border)',
                    borderRadius: 11,
                    padding: '12px 14px',
                    background: 'var(--sig-panel)'
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background: v.color,
                        display: 'block',
                        flex: 'none'
                      }}
                    />
                    <span
                      dir="auto"
                      className="text-[11.5px]"
                      style={{ color: 'var(--sig-muted)' }}
                    >
                      {v.name}
                    </span>
                  </div>
                  <div
                    dir="ltr"
                    className="text-[22px] font-bold mt-1"
                    style={{
                      color: 'var(--sig-ink)',
                      fontFamily: 'Inter, sans-serif',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-.02em'
                    }}
                  >
                    {typeof v.value === 'number' ? compact(v.value) : '—'}
                  </div>
                  {typeof focusRow.shares[i] === 'number' ? (
                    <div
                      dir="ltr"
                      className="text-[11.5px] mt-0.5"
                      style={{ color: 'var(--sig-subtle)' }}
                    >
                      {(focusRow.shares[i] as number).toFixed(1)}% of total
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            {typeof focusRow.change === 'number' ? (
              <div
                dir="ltr"
                className="text-[12.5px] font-semibold"
                style={{
                  color:
                    focusRow.change >= 0
                      ? 'var(--sig-positive)'
                      : 'var(--sig-negative)',
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {focusRow.change >= 0 ? '▲' : '▼'}{' '}
                {Math.abs(focusRow.change).toFixed(1)}%
              </div>
            ) : null}
            <div
              className="text-[11.5px]"
              style={{ color: 'var(--sig-subtle)' }}
            >
              From the figures in this answer — no new query was run.
            </div>
          </div>
        ) : view === 'table' ? (
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarGutter: 'stable'
            }}
          >
            <table
              style={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                width: '100%',
                fontSize: 13
              }}
            >
              <thead>
                <tr>
                  {['', ...series.map((s) => s.name)].map((h, i) => (
                    <th
                      key={`${h}-${i}`}
                      dir="auto"
                      style={{
                        background: 'var(--sig-table-head)',
                        padding: '9px 12px',
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: 'var(--sig-body)',
                        textAlign: i === 0 ? 'left' : 'right',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid var(--sig-border-strong)'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c} className="sig-row">
                    <td
                      dir="auto"
                      style={{
                        padding: '10px 12px',
                        fontWeight: 600,
                        color: 'var(--sig-ink)',
                        whiteSpace: 'nowrap',
                        borderBottom: '1px solid var(--sig-border-row)'
                      }}
                    >
                      {c}
                    </td>
                    {series.map((s) => (
                      <td
                        key={s.name}
                        dir="ltr"
                        style={{
                          padding: '10px 12px',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          color: 'var(--sig-body)',
                          fontFamily: 'Inter, sans-serif',
                          fontVariantNumeric: 'tabular-nums',
                          borderBottom: '1px solid var(--sig-border-row)'
                        }}
                      >
                        {s.byCategory.has(c)
                          ? compact(s.byCategory.get(c) as number)
                          : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Suspense
            fallback={<Skeleton style={{ height }} className="rounded-md" />}
          >
            <div style={{ width: '100%', height }}>
              <Plot
                ref={plotRef}
                data={themedData}
                layout={themedLayout}
                frames={frames}
                config={themedConfig}
                style={{ width: '100%', height: '100%' }}
                useResizeHandler={true}
                onClick={(e: any) => {
                  const point = e?.points?.[0];
                  if (!point) return;
                  const label =
                    point.data?.orientation === 'h' ? point.y : point.x;
                  if (label !== undefined && label !== null) {
                    setFocus(String(label));
                  }
                }}
              />
            </div>
          </Suspense>
        )}

        {!focus && view === 'chart' && categories.length ? (
          <div className="text-[11.5px]" style={{ color: 'var(--sig-subtle)' }}>
            Select a bar to see its figures.
          </div>
        ) : null}
      </div>
    </div>
  );
};
